// "⇄ Likely transfer?" pending review row, drawn identically at every band
// in `#s06`. Confirm posts the pairing; Not a transfer rejects only this
// candidate using its opaque evidence token and leaves ledger activity intact.
import { useRef, useState } from 'react';
import { AmountText } from '@/components/AmountText';
import { Button } from '@moneyskyline/ui';
import { useRejectLink } from '@/api/hooks/useRejectLink';
import type { InboxCandidateReviewItem, InboxTransferReviewItem } from '@/api/hooks/useInboxItems';

type TransferReviewItem = InboxTransferReviewItem | InboxCandidateReviewItem;

export function TransferReviewCard({
  item,
  onConfirm,
  onReject,
  pending,
}: {
  item: TransferReviewItem;
  onConfirm: (candidateId: string, kind: 'transfer' | 'cc_payment') => void;
  onReject?: (candidateId: string) => void;
  pending?: boolean;
}) {
  const candidateId = 'candidate_id' in item ? item.candidate_id : null;
  const evidenceToken = 'evidence_token' in item ? item.evidence_token : null;
  const confirmId = 'candidate_id' in item ? item.candidate_id : item.group_id;

  return (
    <div
      className="rounded-lg border border-border border-l-4 border-l-money-neutral bg-card p-3"
      data-testid="transfer-review-card"
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="text-sm">
          <span className="font-semibold text-money-neutral">⇄ Likely transfer?</span>{' '}
          <span className="text-foreground">
            Checking •{item.leg_a.account_last4} <AmountText value={item.leg_a.amount} variant="transfer" /> {item.leg_a.posted_at} ·
            Brokerage •{item.leg_b.account_last4} <AmountText value={item.leg_b.amount} variant="transfer" /> {item.leg_b.posted_at}
          </span>
        </div>
        <div className="flex gap-2">
          <Button type="button" size="sm" disabled={pending} onClick={() => onConfirm(confirmId, item.link_kind)}>
            Confirm
          </Button>
          {candidateId && evidenceToken ? (
            <CandidateRejectAction candidateId={candidateId} evidenceToken={evidenceToken} onReject={onReject} />
          ) : (
            <Button type="button" size="sm" variant="outline" disabled aria-label="Not a transfer">
              Not a transfer
            </Button>
          )}
        </div>
      </div>
      <p className="mt-2 text-xs text-muted-foreground">
        {candidateId
          ? 'Rejecting this suggestion leaves both transactions as ordinary activity.'
          : 'Not a transfer is not available yet. Until you confirm, both transactions continue to count as ordinary activity.'}
      </p>
    </div>
  );
}

function CandidateRejectAction({
  candidateId,
  evidenceToken,
  onReject,
}: {
  candidateId: string;
  evidenceToken: string;
  onReject?: (candidateId: string) => void;
}) {
  const rejectLink = useRejectLink();
  const rejectButtonRef = useRef<HTMLElement>(null);
  const [rejectError, setRejectError] = useState<string | null>(null);

  function rejectCandidate() {
    setRejectError(null);
    rejectLink.mutate(
      { candidateId, evidenceToken },
      {
        onSuccess: (result) => {
          rejectButtonRef.current?.focus();
          onReject?.(result.data.candidate_id);
        },
        onError: () => {
          setRejectError('We couldn’t confirm whether this suggestion was rejected. Refresh before trying again.');
        },
      },
    );
  }

  return (
    <>
      <span ref={rejectButtonRef} className="inline-flex">
        <Button
          type="button"
          size="sm"
          variant="outline"
          disabled={rejectLink.isPending}
          aria-busy={rejectLink.isPending}
          aria-label="Not a transfer"
          onClick={rejectCandidate}
        >
          Not a transfer
        </Button>
      </span>
      {rejectError && <p className="mt-2 basis-full text-sm text-destructive" role="alert">{rejectError}</p>}
    </>
  );
}

export default TransferReviewCard;
